import exportRunService from './exportRunService';

const POLL_MS = 1500;
const MAX_POLL_ATTEMPTS = 800;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const defaultFileName = () => `Reporte_Resultados_${new Date().toISOString().slice(0, 10)}.xlsx`;

function filenameFromHeaders(headers) {
  const disposition = headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch (_) {
      return match[1];
    }
  }
  return null;
}

async function blobToJson(blob) {
  if (!blob || typeof blob.text !== 'function') return null;
  const text = await blob.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || defaultFileName();
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function generateResultsReportExcel(params, { onProgress } = {}) {
  onProgress?.({
    phase: 'background',
    status: 'processing',
    progress_percent: 5,
    message: 'Iniciando exportación…',
  });

  let response;
  try {
    response = await exportRunService.prepareResultsReport(params);
  } catch (error) {
    const payload = await blobToJson(error?.response?.data);
    throw new Error(payload?.message || error?.message || 'No se pudo iniciar la exportación');
  }

  const contentType = String(response?.headers?.['content-type'] || '');

  if (contentType.includes('spreadsheetml') || contentType.includes('octet-stream')) {
    onProgress?.({
      phase: 'background',
      status: 'completed',
      progress_percent: 100,
      message: 'Archivo listo',
    });
    saveBlob(response.data, filenameFromHeaders(response.headers));
    return { sync: true };
  }

  const payload = await blobToJson(response.data);
  if (!payload?.success || !payload?.data?.id) {
    throw new Error(payload?.message || 'No se pudo iniciar la exportación');
  }

  let run = payload.data;
  let attempts = 0;

  while (attempts < MAX_POLL_ATTEMPTS) {
    onProgress?.({
      phase: 'background',
      status: run.status,
      progress_percent: run.progress_percent ?? 5,
      items_processed: run.items_processed ?? 0,
      items_total: run.items_total ?? 0,
      message:
        run.status === 'queued'
          ? 'En cola, esperando al worker…'
          : `Generando Excel (${run.items_processed ?? 0}/${run.items_total ?? 0} filas)…`,
      run_id: run.id,
    });

    if (run.status === 'completed') {
      onProgress?.({
        phase: 'background',
        status: 'completed',
        progress_percent: 98,
        items_processed: run.items_total,
        items_total: run.items_total,
        message: 'Descargando archivo…',
        run_id: run.id,
      });
      const fileRes = await exportRunService.downloadResultsReportFile(run.id);
      saveBlob(fileRes.data, run.file_name || filenameFromHeaders(fileRes.headers));
      onProgress?.({
        phase: 'background',
        status: 'completed',
        progress_percent: 100,
        items_processed: run.items_total,
        items_total: run.items_total,
        message: 'Archivo listo',
        run_id: run.id,
      });
      return { sync: false, runId: run.id };
    }

    if (run.status === 'failed') {
      throw new Error(run.error_message || 'Error al generar el Excel de resultados');
    }

    await sleep(POLL_MS);
    const pollRes = await exportRunService.get(run.id);
    if (!pollRes?.success || !pollRes?.data) {
      throw new Error(pollRes?.message || 'No se pudo consultar el progreso');
    }
    run = pollRes.data;
    attempts++;
  }

  throw new Error('Tiempo de espera agotado al generar el Excel de resultados');
}
